import { useState, useEffect, useCallback } from 'react';
import { collection, addDoc, query, where, orderBy, getDocs, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Hook personnalisé pour gérer la génération et la persistance des synthèses Academy
 */
const useAcademySynthesis = (userId) => {
  const [syntheses, setSyntheses] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Charger les synthèses existantes depuis Firebase
  const loadSyntheses = useCallback(async () => {
    if (!userId) return;

    try {
      setLoading(true);
      const synthesesRef = collection(db, 'academySyntheses');
      const q = query(
        synthesesRef,
        where('userId', '==', userId),
        orderBy('generatedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const synthesesData = {};
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        synthesesData[data.objectiveId] = {
          id: doc.id,
          ...data
        };
      });
      
      setSyntheses(synthesesData);
    } catch (err) {
      console.error('Erreur chargement synthèses:', err);
      setError('Erreur lors du chargement des synthèses');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  // Sauvegarder une synthèse dans Firebase
  const saveSynthesis = useCallback(async (synthesisData) => {
    if (!userId) return null;

    try {
      const synthesisWithUser = {
        ...synthesisData,
        userId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Vérifier si une synthèse existe déjà pour cet objectif
      const existingSynthesis = syntheses[synthesisData.objectiveId];
      
      let docRef;
      if (existingSynthesis) {
        // Mettre à jour la synthèse existante
        docRef = doc(db, 'academySyntheses', existingSynthesis.id);
        await setDoc(docRef, {
          ...synthesisWithUser,
          updatedAt: new Date().toISOString()
        }, { merge: true });
      } else {
        // Créer une nouvelle synthèse
        docRef = await addDoc(collection(db, 'academySyntheses'), synthesisWithUser);
      }

      // Mettre à jour le state local
      setSyntheses(prev => ({
        ...prev,
        [synthesisData.objectiveId]: {
          ...synthesisWithUser,
          id: docRef.id
        }
      }));

      return docRef.id;
    } catch (err) {
      console.error('Erreur sauvegarde synthèse:', err);
      setError('Erreur lors de la sauvegarde de la synthèse');
      return null;
    }
  }, [userId, syntheses]);

  // Générer une synthèse pour un objectif et des vidéos donnés
  const generateSynthesis = useCallback(async (videos, objective) => {
    if (!videos.length || !objective || !userId) {
      throw new Error('Données manquantes pour générer la synthèse');
    }

    setLoading(true);
    setError(null);

    try {
      // Vérifier si une synthèse existe déjà pour cet objectif
      const existingSynthesis = syntheses[objective.id];
      if (existingSynthesis) {
        // Vérifier si les vidéos ont changé significativement
        const videosChanged = checkVideosChanged(existingSynthesis.videoIds, videos);
        if (!videosChanged) {
          return existingSynthesis;
        }
      }

      // Préparer les données pour l'analyse
      const videoContext = videos.map(video => `
        [${video.title}] - Score: ${video.semanticScore || 5}/10
        Description: ${video.description || 'Aucune description'}
        Durée: ${video.duration || 'Inconnue'}
        Thème: ${video.theme || 'Général'}
        URL: ${video.url}
      `).join('\n\n');

      // Prompt optimisé pour générer un texte fluide avec liens intégrés
      const prompt = `Tu es un expert en analyse de contenu pour le développement professionnel et personnel.

OBJECTIF DE L'UTILISATEUR: "${objective.title}"
${objective.description ? `DESCRIPTION: ${objective.description}` : ''}

SOURCES DISPONIBLES (${videos.length} vidéos):
${videoContext}

GÉNÈRE UNE SYNTHÈSE FLUIDE ET NATURELLE QUI :

- Présente les sources vidéos les plus pertinentes pour atteindre l'objectif
- Intègre des liens cliquables vers les vidéos dans le texte
- Explique pourquoi chaque source est utile pour l'objectif spécifique
- Offre des conseils pratiques pour tirer le meilleur parti des ressources
- Utilise un style narratif continu sans titres ni sous-titres

FORMAT REQUIS :
- Texte narratif continu (pas de sections structurées)
- Liens intégrés au format : [Titre de la vidéo](URL de la vidéo)
- Style conversationnel et fluide
- Focus sur l'utilité pratique pour l'utilisateur

Exemple de structure souhaitée :
"Pour votre objectif de [objectif], j'ai identifié plusieurs ressources pertinentes. La vidéo [Titre vidéo 1](URL1) vous offre une vision d'ensemble inspirante, particulièrement utile pour comprendre les concepts fondamentaux. En complément, [Titre vidéo 2](URL2) fournit des conseils pratiques applicables immédiatement..."`;

      // Utiliser votre système askGPT existant
      const response = await window.askGPT?.(prompt) || 
                      await simulateGPTResponse(videos, objective);

      const synthesisData = {
        id: `synthesis-${Date.now()}`,
        content: response,
        objectiveId: objective.id,
        objectiveTitle: objective.title,
        videoCount: videos.length,
        videoIds: videos.map(v => v.id || v.url),
        generatedAt: new Date().toISOString(),
        concepts: extractConcepts(response),
        estimatedDuration: estimateDuration(videos),
        difficultyLevel: estimateDifficulty(videos),
        metadata: {
          totalVideos: videos.length,
          avgSemanticScore: calculateAverageScore(videos),
          videoThemes: extractThemes(videos)
        }
      };

      // Sauvegarder dans Firebase
      await saveSynthesis(synthesisData);

      return synthesisData;

    } catch (err) {
      console.error('Erreur génération synthèse:', err);
      setError('Erreur lors de la génération de la synthèse');
      throw err;
    } finally {
      setLoading(false);
    }
  }, [userId, syntheses, saveSynthesis]);

  // Fonctions utilitaires
  const checkVideosChanged = (existingVideoIds, newVideos) => {
    if (!existingVideoIds || !newVideos) return true;
    
    const newVideoIds = newVideos.map(v => v.id || v.url);
    if (existingVideoIds.length !== newVideoIds.length) return true;
    
    return !existingVideoIds.every(id => newVideoIds.includes(id));
  };

  const extractConcepts = (content) => {
    const concepts = new Set();
    const lines = content.split('\n');
    
    lines.forEach(line => {
      // Extraire les concepts des listes à puces
      if ((line.includes('-') || line.includes('•')) && !line.includes('http')) {
        const concept = line
          .replace(/[-•\d.📋🎯🔄⏱️💡📈🔗]/g, '')
          .replace(/\[.*?\]/g, '')
          .trim();
        
        if (concept && concept.length > 3 && concept.length < 50) {
          concepts.add(concept);
        }
      }
    });
    
    return Array.from(concepts).slice(0, 7);
  };

  const estimateDuration = (videos) => {
    return videos.reduce((total, video) => {
      // Si le backend fournit durationInMinutes, l'utiliser directement
      if (video.durationInMinutes) {
        return total + video.durationInMinutes;
      }
      
      // Sinon, estimation simple basée sur le type de contenu
      // Vidéos éducatives typiques : 10-20 minutes
      return total + 15; // Estimation par défaut
    }, 0);
  };

  const estimateDifficulty = (videos) => {
    const scores = videos.map(v => v.semanticScore || 5);
    const avgScore = scores.reduce((a, b) => a + b, 0) / scores.length;
    
    if (avgScore >= 8) return 'Avancé';
    if (avgScore >= 6) return 'Intermédiaire';
    return 'Débutant';
  };

  const calculateAverageScore = (videos) => {
    const scores = videos.map(v => v.semanticScore || 5);
    return scores.reduce((a, b) => a + b, 0) / scores.length;
  };

  const extractThemes = (videos) => {
    const themes = new Set();
    videos.forEach(video => {
      if (video.theme) themes.add(video.theme);
    });
    return Array.from(themes);
  };

  // Simulation de réponse GPT (fallback) - version texte fluide avec liens
  const simulateGPTResponse = async (videos, objective) => {
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    const totalMinutes = estimateDuration(videos);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = Math.round(totalMinutes % 60);
    
    // Générer un texte fluide avec liens intégrés
    return `Pour votre objectif de **${objective.title}**, j'ai identifié plusieurs ressources vidéos particulièrement pertinentes qui vous aideront à progresser efficacement.

La vidéo [${videos[0]?.title || 'Vidéo principale'}](${videos[0]?.url || '#'}) vous offre une excellente vision d'ensemble, idéale pour comprendre les concepts fondamentaux et vous inspirer dans votre démarche. Son approche pratique et ses exemples concrets vous donneront une base solide pour démarrer.

En complément, [${videos[1]?.title || 'Vidéo complémentaire'}](${videos[1]?.url || '#'}) se concentre sur les aspects opérationnels avec des conseils applicables immédiatement. Cette ressource est particulièrement utile si vous souhaitez passer rapidement à l'action et voir des résultats concrets.

Pour approfondir certains aspects spécifiques, [${videos[2]?.title || 'Vidéo spécialisée'}](${videos[2]?.url || '#'}) apporte une expertise technique précieuse avec des insights avancés qui vous permettront d'aller plus loin dans votre compréhension du sujet.

Je vous recommande de commencer par la première vidéo pour avoir une vision globale, puis d'explorer les autres selon vos besoins spécifiques. L'ensemble représente environ ${hours > 0 ? `${hours}h${minutes > 0 ? `${minutes}min` : ''}` : `${minutes}min`} de contenu que vous pouvez répartir en 2-3 sessions par semaine pour une assimilation optimale.

N'hésitez pas à prendre des notes pendant le visionnage et à identifier les conseils que vous pouvez appliquer immédiatement dans votre contexte. Cette sélection de ${videos.length} sources vous offre un parcours complet pour avancer concrètement vers votre objectif.`;
  };

  // Effet pour charger les synthèses au montage
  useEffect(() => {
    if (userId) {
      loadSyntheses();
    }
  }, [userId, loadSyntheses]);

  return {
    syntheses,
    loading,
    error,
    generateSynthesis,
    saveSynthesis,
    loadSyntheses
  };
};

export default useAcademySynthesis;
